/** Fehler aus der API mit deutscher Meldung. */
export class ApiError extends Error {
  constructor(public status: number, message: string, public code: string = 'ERROR', public details?: unknown) {
    super(message);
  }
}

/** Wird bei 401 (Sitzung abgelaufen) aufgerufen – setzt AuthProvider. */
let onUnauthorized: ((code: string) => void) | null = null;
export function setUnauthorizedHandler(fn: (code: string) => void) {
  onUnauthorized = fn;
}

/** Für eigene Requests (z. B. XHR-Uploads): meldet eine abgelaufene Sitzung an die Anmeldung. */
export function notifyUnauthorized(code = 'UNAUTHORIZED') {
  onUnauthorized?.(code);
}

async function request<T>(method: string, url: string, body?: unknown, opts: { signal?: AbortSignal; raw?: boolean } = {}): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(url.startsWith('/api') ? url : `/api${url}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined || isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen.', 'NETWORK');
  }
  if (opts.raw) return res as unknown as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = (data as { error?: { message?: string; code?: string; details?: unknown } } | null)?.error;
    const apiErr = new ApiError(res.status, err?.message || `Fehler ${res.status}`, err?.code, err?.details);
    // 401 = Sitzung abgelaufen/Passwortwechsel; 403 TWO_FACTOR_SETUP_REQUIRED = Admin muss erst 2FA einrichten
    if ((res.status === 401 || apiErr.code === 'TWO_FACTOR_SETUP_REQUIRED') && onUnauthorized && !url.includes('/auth/login')) onUnauthorized(apiErr.code);
    throw apiErr;
  }
  return data as T;
}

export const api = {
  get: <T = unknown>(url: string, opts?: { signal?: AbortSignal }) => request<T>('GET', url, undefined, opts),
  post: <T = unknown>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  put: <T = unknown>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  patch: <T = unknown>(url: string, body?: unknown) => request<T>('PATCH', url, body ?? {}),
  del: <T = unknown>(url: string) => request<T>('DELETE', url),
  /** Datei-Upload (multipart/form-data). */
  upload: <T = unknown>(url: string, form: FormData) => request<T>('POST', url, form),
  /** Rohe Response, z. B. für Datei-Downloads. */
  raw: (url: string) => request<Response>('GET', url, undefined, { raw: true }),
};

/** Baut eine Query-String aus einem Objekt (undefined/null/'' werden ausgelassen). */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Lädt eine Datei vom Server herunter (nutzt Content-Disposition-Dateinamen). */
export async function downloadFile(url: string, fallbackName = 'download') {
  const res = await api.raw(url);
  if (!res.ok) {
    let msg = `Fehler ${res.status}`;
    try { msg = (await res.json()).error?.message ?? msg; } catch { /* ignore */ }
    throw new ApiError(res.status, msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
